<template>
	<component
		:is="rootTag"
		v-if="requiresOrganizationAttention"
		:class="rootClass"
		:role="isNavbarPlacement ? 'none' : undefined"
	>
		<button
			type="button"
			class="attention-status__button"
			:aria-label="attentionLabel"
			@click.prevent="onAttentionClick"
		>
			<span class="attention-pill" role="presentation">
				<span class="attention-pill__label">{{ attentionLabel }}</span>
			</span>
		</button>
	</component>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import {
	SETTINGS_ATTENTION_PILL_LABEL,
	SETTINGS_SECTION_IDS,
} from '@/config/settingsConstants'

/**
 * Navbar attention indicator and navigation entry point.
 * Loads organization profile from the settings store and links
 * users to the section that needs attention.
 */
export default {
	computed: {
		attentionLabel() {
			return SETTINGS_ATTENTION_PILL_LABEL
		},

		isNavbarPlacement() {
			return this.placement === 'navbar'
		},

		rootTag() {
			return this.isNavbarPlacement ? 'li' : 'div'
		},

		rootClass() {
			return this.isNavbarPlacement
				? 'nav-link attention-status'
				: 'inbox-title-attention'
		},

		activePartnerId() {
			return this.partner?.id || this.$route.params.partner
		},

		resolvedOrganizationId() {
			if (this.isOrganizationOnlyAdmin) {
				return this.organizationId
			}

			return this.partner?.organization_id || this.organizationId
		},

		isOrgAdminOnly() {
			return (
				this.isOrganizationOnlyAdmin ||
				Boolean(this.organizationId && !this.access)
			)
		},

		attentionRoute() {
			const organizationId =
				this.organizationId || this.resolvedOrganizationId

			if (this.isOrgAdminOnly && organizationId) {
				return {
					name: 'app.organization',
					params: {
						organizationId,
					},
				}
			}

			const section =
				this.requiresOrganizationProfileAttention && this.isOrgAdmin
					? SETTINGS_SECTION_IDS.ORGANIZATION
					: SETTINGS_SECTION_IDS.GENERAL_INFORMATION

			return {
				name: 'app.settings',
				params: {
					partner: this.activePartnerId,
					section,
				},
			}
		},

		...mapGetters({
			partner: 'partners/active',
			requiresOrganizationAttention:
				'settings/requiresOrganizationAttention',
			requiresOrganizationProfileAttention:
				'settings/requiresOrganizationProfileAttention',
			isOrganizationOnlyAdmin: 'auth/isOrganizationOnlyAdmin',
			isOrgAdmin: 'auth/isOrgAdmin',
			organizationId: 'auth/organizationId',
			clarusAppType: 'partners/clarusAppType',
			access: 'auth/hasOfficeManagerAccess',
		}),
	},

	methods: {
		canNavigateToAttentionRoute() {
			if (this.isOrganizationOnlyAdmin || this.isOrgAdmin) {
				return true
			}

			if (!this.clarusAppType) {
				this.$alert.error('Please contact your system administrator.')
				return false
			}

			return Boolean(this.access)
		},

		isCurrentRoute(target) {
			if (this.$route.name !== target.name) {
				return false
			}

			return Object.entries(target.params || {}).every(
				([key, value]) =>
					String(this.$route.params[key] || '') === String(value)
			)
		},

		async navigateToAttentionRoute() {
			const target = this.attentionRoute

			if (this.isCurrentRoute(target)) {
				return
			}

			const navigate =
				this.$route.name === target.name ? 'replace' : 'push'

			await this.$router[navigate](target).catch(() => {})
		},

		shouldOpenOrganizationPanelFromAttention() {
			if (this.isOrganizationOnlyAdmin || this.isOrgAdmin) {
				return false
			}

			return (
				this.attentionRoute.params.section ===
				SETTINGS_SECTION_IDS.GENERAL_INFORMATION
			)
		},

		async onAttentionClick() {
			if (!this.canNavigateToAttentionRoute()) {
				return
			}

			const target = this.attentionRoute
			const shouldOpenOrganizationPanel = this.shouldOpenOrganizationPanelFromAttention()

			if (shouldOpenOrganizationPanel) {
				await this.openOrganizationRestrictedAccessFromAttention()
			}

			await this.navigateToAttentionRoute()

			if (shouldOpenOrganizationPanel && this.isCurrentRoute(target)) {
				await this.$nextTick()
				await this.openOrganizationRestrictedAccessFromAttention()
			}
		},

		...mapActions('settings', {
			syncOrganizationProfilePolling: 'syncOrganizationProfilePolling',
			openOrganizationRestrictedAccessFromAttention:
				'openOrganizationRestrictedAccessFromAttention',
		}),
	},

	name: 'AttentionStatus',

	props: {
		placement: {
			default: 'navbar',
			type: String,
			validator(value) {
				return ['inbox', 'navbar', 'title'].includes(value)
			},
		},
	},

	watch: {
		resolvedOrganizationId: {
			immediate: true,
			handler() {
				this.syncOrganizationProfilePolling()
			},
		},
	},
}
</script>
